import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/** Ключи под кэш */
const LS_DICT_KEY = "i18n-uz-dict";
const LS_VER_KEY  = "i18n-uz-version";

/** Если сервер отдаёт версию — прокиньте её сюда из index.html/SSR */
const getExpectedVersion = () => window.__I18N_VERSION__ || null;

/** Функция загрузки словаря. Подмените URL/headers под свой API. */
// i18n/TextProvider.jsx (фрагмент)

async function fetchTexts() {
    const res = await fetch("https://apitr.subxonqulov.uz/api/tizimdaginomlar/", { credentials: "include" });
    if (!res.ok) throw new Error(`i18n fetch failed: ${res.status}`);
    const json = await res.json();
  
    // 1) Если сервер уже возвращает плоский словарь — просто вернём как есть
    if (json && typeof json === "object" && !Array.isArray(json) && json.dict) {
      return { version: json.version || null, dict: mapServerArrayToDict(json.dict) };
    }
    if (json && typeof json === "object" && !Array.isArray(json) && hasNomQiymatShape(json)) {
      // единичный объект
      return { version: json.version || null, dict: mapServerArrayToDict([json]) };
    }
    if (Array.isArray(json)) {
      // массив записей {id, nom, qiymat, ...}
      return { version: null, dict: mapServerArrayToDict(json) };
    }
    // fallback: если пришёл уже словарь
    return { version: json.version || null, dict: json };
  }
  
  // Проверка формы записи
  function hasNomQiymatShape(o) {
    return o && typeof o === "object" && ("nom" in o) && ("qiymat" in o);
  }
  
  // Нормализация ключа (опционально)
  // Оставьте как есть, если ключи должны совпадать 1:1 с `nom`
  function normalizeKey(k) {
    // пример: обрезать пробелы вокруг
    return String(k ?? "").trim();
    // Если нужно нижний регистр:
    // return String(k ?? "").trim().toLowerCase();
    // Если нужны безопасные ключи без пробелов:
    // return String(k ?? "").trim().replace(/\s+/g, "_");
  }
  
  // Маппинг: [{ nom, qiymat }, ...] -> { [nom]: qiymат }
  function mapServerArrayToDict(arr) {
    const dict = {};
    for (const row of arr) {
      if (!row) continue;
      const keyRaw = row.nom;
      const val = row.qiymat;
      if (keyRaw == null || val == null) continue; // пропускаем пустые
      const key = normalizeKey(keyRaw);
      // при дублях последний побеждает; можно поменять стратегию (см. ниже)
      dict[key] = String(val);
    }
    return dict;
  }
  

function loadFromLS() {
  try {
    const dict = JSON.parse(localStorage.getItem(LS_DICT_KEY) || "null");
    const version = localStorage.getItem(LS_VER_KEY);
    if (dict && typeof dict === "object") return { dict, version };
  } catch {}
  return { dict: null, version: null };
}

function saveToLS({ dict, version }) {
  try {
    localStorage.setItem(LS_DICT_KEY, JSON.stringify(dict || {}));
    if (version) localStorage.setItem(LS_VER_KEY, version);
  } catch {}
}

const I18nCtx = createContext({
  ready: false,
  dict: {},
  t: (key, fallbackOrOpts) => key,
});

/** Провайдер: тянет словарь, сверяет версию, кладёт в LS, даёт t() */
export function TextProvider({ children }) {
  const [state, setState] = useState(() => {
    const { dict, version } = loadFromLS();
    return { ready: !!dict, dict: dict || {}, version: version || null };
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const expected = getExpectedVersion();
        // Если версия совпадает — можно не тянуть (опционально)
        if (expected && state.version && state.version === expected) {
          if (!state.ready) setState((s) => ({ ...s, ready: true }));
          return;
        }
        const { version, dict } = await fetchTexts();
        if (cancelled) return;
        saveToLS({ dict, version: version || expected || "" });
        setState({ ready: true, dict, version: version || expected || "" });
      } catch {
        // Фоллбек: если в LS уже что-то было — показываем; иначе хотя бы не блокируем UI
        if (!state.ready) setState((s) => ({ ...s, ready: true }));
      }
    })();
    return () => { cancelled = true; };
  }, []); // грузим единожды при старте

  const t = useMemo(() => {
    // поддержка простых плейсхолдеров: "Salom, {name}"
    const interpolate = (str, params) =>
      typeof str === "string" && params
        ? str.replace(/\{(\w+)\}/g, (_, k) => (params[k] ?? `{${k}}`))
        : str;

    return (key, fallbackOrOpts) => {
      if (!key) return "";
      // допускаем вызовы вида t('next', 'Keyingi') ИЛИ t('welcome', {fallback:'...', params:{...}})
      let fallback, params;
      if (typeof fallbackOrOpts === "string") fallback = fallbackOrOpts;
      if (typeof fallbackOrOpts === "object" && fallbackOrOpts) {
        fallback = fallbackOrOpts.fallback;
        params = fallbackOrOpts.params;
      }
      const val = state.dict?.[key];
      if (val == null) return fallback ?? key; // если нет ключа — ключ как текст
      return interpolate(val, params);
    };
  }, [state.dict]);

  const value = useMemo(() => ({ ready: state.ready, dict: state.dict, t }), [state.ready, state.dict, t]);

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useT() {
  const ctx = useContext(I18nCtx);
  return ctx.t;
}

export function useI18nReady() {
  const ctx = useContext(I18nCtx);
  return ctx.ready;
}

// Контекст для управления прогрессом (tartib и mashqtartib)
export const ProgressContext = createContext(null);

export function ProgressProvider({ children }) {
  const [progress, setProgress] = useState({});

  // progress[fanId] = { tartib, mashqtartib, id }
  // fanId это ID предмета

  useEffect(() => {
    // Загружаем прогресс из localStorage
    try {
      const saved = localStorage.getItem('user:progress');
      if (saved) {
        setProgress(JSON.parse(saved));
      }
    } catch (e) {
      console.warn('Error loading progress:', e);
    }
  }, []);

  const updateProgress = (fanId, tartib, mashqtartib, xolatId) => {
    setProgress(prev => {
      const updated = {
        ...prev,
        [fanId]: { tartib, mashqtartib, id: xolatId }
      };
      localStorage.setItem('user:progress', JSON.stringify(updated));
      return updated;
    });
  };

  const getProgress = (fanId) => {
    return progress[fanId] || null;
  };

  return (
    <ProgressContext.Provider value={{ progress, updateProgress, getProgress }}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress должен быть в ProgressProvider');
  return ctx;
}
